import { Check, CreditCard, Landmark } from "lucide-react";
import type { BankAccount } from "@/types/bankReconciliation";

type Props = {
  accounts: BankAccount[];
  activeAccountId: string | null;
  onSelect: (accountId: string) => void;
};

function AccountCard({
  account,
  active,
  onSelect,
}: {
  account: BankAccount;
  active: boolean;
  onSelect: () => void;
}) {
  const Icon = account.accountType === "credit_card" ? CreditCard : Landmark;
  const done = account.unreconciledCount === 0;
  const progress =
    account.transactionCount > 0
      ? account.reconciledCount / account.transactionCount
      : 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={`w-56 shrink-0 rounded-xl border p-3 text-left transition ${
        active
          ? "border-emerald-500 bg-emerald-50/40 shadow-sm ring-1 ring-emerald-500"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
            active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
          }`}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        {done ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
            <Check className="h-3 w-3" aria-hidden />
            Done
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
            {account.unreconciledCount} left
          </span>
        )}
      </div>

      <p className="mt-2 truncate text-sm font-semibold text-slate-900">
        {account.label}
      </p>
      <p className="mt-0.5 truncate text-xs text-slate-500">
        {[
          account.institution,
          account.mask ? `···· ${account.mask}` : null,
        ]
          .filter(Boolean)
          .join(" · ") || " "}
      </p>

      <div className="mt-2 flex items-center gap-2">
        <div className="h-1 flex-1 overflow-hidden rounded-full bg-slate-100">
          <div
            className={`h-full rounded-full ${done ? "bg-emerald-500" : "bg-emerald-400"}`}
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
        <span className="text-[11px] font-medium tabular-nums text-slate-400">
          {account.currency}
        </span>
      </div>
    </button>
  );
}

function AccountGroup({
  title,
  accounts,
  activeAccountId,
  onSelect,
}: {
  title: string;
  accounts: BankAccount[];
  activeAccountId: string | null;
  onSelect: (accountId: string) => void;
}) {
  if (accounts.length === 0) return null;
  return (
    <div className="min-w-0">
      <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {title}
      </h3>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {accounts.map((account) => (
          <AccountCard
            key={account.id}
            account={account}
            active={account.id === activeAccountId}
            onSelect={() => onSelect(account.id)}
          />
        ))}
      </div>
    </div>
  );
}

export function AccountRail({ accounts, activeAccountId, onSelect }: Props) {
  const banks = accounts.filter((account) => account.accountType !== "credit_card");
  const cards = accounts.filter((account) => account.accountType === "credit_card");

  return (
    <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
      <AccountGroup
        title="Banks"
        accounts={banks}
        activeAccountId={activeAccountId}
        onSelect={onSelect}
      />
      <AccountGroup
        title="Credit cards"
        accounts={cards}
        activeAccountId={activeAccountId}
        onSelect={onSelect}
      />
    </div>
  );
}
